/**
 * Cohort Manager Component
 * Manages leadership development cohorts with full CRUD operations
 */

import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Users, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import CohortFormModal from './CohortFormModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import Toast from './Toast';
import PaginationControls from './PaginationControls';
import * as cohortAPI from '../../services/cohortAPI';
import './AdminComponents.css';

const CohortManager = () => {
  const { getAccessToken } = useAuth();
  const [cohorts, setCohorts] = useState([]);
  const [filteredCohorts, setFilteredCohorts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterProgramLevel, setFilterProgramLevel] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);
  
  const ITEMS_PER_PAGE = 15;

  const programLevels = [
    'FRONTLINE_LEADERS',
    'INFORMAL_LEADERS',
    'LEADERS_OF_LEADERS',
    'EXPERT_LEADERS',
    'VISIONARY_LEADERS'
  ];
  const statusOptions = ['DRAFT', 'ACTIVE', 'COMPLETED', 'ARCHIVED'];

  // Helper to format program level display names
  const formatProgramLevel = (level) => {
    const labels = {
      'FRONTLINE_LEADERS': 'Frontline Leaders',
      'INFORMAL_LEADERS': 'Informal Leaders',
      'LEADERS_OF_LEADERS': 'Leaders of Leaders',
      'EXPERT_LEADERS': 'Expert Leaders',
      'VISIONARY_LEADERS': 'Visionary Leaders',
      // Legacy support for old values
      'EMERGING': 'Emerging Leaders (Legacy)',
      'EXPERIENCED': 'Experienced Leaders (Legacy)',
      'EXECUTIVE': 'Executive Leaders (Legacy)'
    };
    return labels[level] || level;
  };

  // Set up API token getter
  useEffect(() => {
    cohortAPI.setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Load cohorts from API
  useEffect(() => {
    loadCohorts();
  }, [filterStatus, filterProgramLevel]);

  // Apply client-side filtering and pagination
  useEffect(() => {
    let filtered = [...cohorts];
    
    // Apply search filter (case-insensitive)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(cohort => 
        cohort.name.toLowerCase().includes(query)
      );
    }
    
    setFilteredCohorts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [cohorts, searchQuery]);

  const loadCohorts = async () => {
    try {
      setLoading(true);
      
      // Build status filter for API
      // If ALL, pass 'all' to backend, otherwise pass the specific status
      let statusParam = filterStatus === 'ALL' ? 'all' : filterStatus.toLowerCase();
      
      // Load ALL cohorts (no pagination limit) so search works across all data
      const response = await cohortAPI.getCohorts({
        status: statusParam,
        program_level: filterProgramLevel !== 'ALL' ? filterProgramLevel : undefined,
        page: 1,
        limit: 1000 // Load all cohorts
      });
      
      // API returns { success, data: { cohorts, pagination }, timestamp }
      const cohortsList = response.data?.cohorts || response.cohorts || [];
      
      setCohorts(cohortsList);
    } catch (err) {
      console.error('Error loading cohorts:', err);
      showToast('Failed to load cohorts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleCreateCohort = async (formData) => {
    try {
      setSaving(true);
      await cohortAPI.createCohort(formData);
      setShowCreateModal(false);
      showToast('Cohort created successfully!');
      // Reload cohorts to get fresh data with participant counts
      await loadCohorts();
    } catch (err) {
      console.error('Error creating cohort:', err);
      showToast(err.response?.data?.error || err.response?.data?.message || 'Failed to create cohort', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCohort = async (formData) => {
    try {
      setSaving(true);
      await cohortAPI.updateCohort(selectedCohort.id, formData);
      setShowEditModal(false);
      setSelectedCohort(null);
      showToast('Cohort updated successfully!');
      // Reload cohorts to get fresh data with participant counts
      await loadCohorts();
    } catch (err) {
      console.error('Error updating cohort:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to update cohort';
      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCohort = async () => {
    try {
      setSaving(true);
      await cohortAPI.deleteCohort(selectedCohort.id);
      setCohorts(prev => prev.filter(c => c.id !== selectedCohort.id));
      setShowDeleteModal(false);
      setSelectedCohort(null);
      showToast('Cohort deleted successfully!');
    } catch (err) {
      console.error('Error deleting cohort:', err);
      showToast(err.response?.data?.message || 'Failed to delete cohort', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: { bg: '#fef3c7', color: '#d97706', border: '#d97706' },
      ACTIVE: { bg: '#d1fae5', color: '#059669', border: '#059669' },
      COMPLETED: { bg: '#dbeafe', color: '#1e40af', border: '#1e40af' },
      ARCHIVED: { bg: '#f3f4f6', color: '#6b7280', border: '#9ca3af' }
    };
    const style = styles[status] || styles.DRAFT;
    
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        background: style.bg,
        color: style.color,
        border: `2px solid ${style.border}`
      }}>
        {status}
      </span>
    );
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredCohorts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCohorts = filteredCohorts.slice(startIndex, endIndex);

  return (
    <AdminLayout>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: '700', 
            color: '#111827',
            marginBottom: '0.5rem'
          }}>
            Cohort Management
          </h1>
          <p style={{ color: '#6b7280', fontSize: '1rem', margin: 0 }}>
            Manage leadership development cohorts and track participant progress
          </p>
        </div>

        {/* Filter Bar */}
        <div style={{
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            {/* Search */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.75rem', 
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Search
              </label>
              <div style={{ position: 'relative' }}>
                <Search 
                  size={18} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.75rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#9ca3af'
                  }} 
                />
                <input
                  type="text"
                  placeholder="Search cohorts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.75rem', 
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  background: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <option value="ALL">All Statuses</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {/* Program Level Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.75rem', 
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Program Level
              </label>
              <select
                value={filterProgramLevel}
                onChange={(e) => setFilterProgramLevel(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  background: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <option value="ALL">All Levels</option>
                {programLevels.map(level => (
                  <option key={level} value={level}>{formatProgramLevel(level)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Create Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
              }}
            >
              <Plus size={18} />
              <span>Create Cohort</span>
            </button>
          </div>
        </div>

        {/* Cohorts List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #10b981',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading cohorts...</p>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : filteredCohorts.length === 0 ? (
          <div style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '4rem 2rem',
            textAlign: 'center'
          }}>
            <Users size={48} color="#9ca3af" style={{ margin: '0 auto 1rem' }} />
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
              No cohorts found
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
              {searchQuery || filterStatus !== 'ALL' || filterProgramLevel !== 'ALL'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first cohort'}
            </p>
            {!searchQuery && filterStatus === 'ALL' && filterProgramLevel === 'ALL' && (
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <Plus size={18} />
                <span>Create First Cohort</span>
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {paginatedCohorts.map(cohort => (
              <div
                key={cohort.id}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                      {cohort.name}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {formatProgramLevel(cohort.program_level)}
                      </span>
                      {getStatusBadge(cohort.status)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={async () => {
                        try {
                          // Fetch full cohort details including participants
                          const response = await cohortAPI.getCohort(cohort.id);
                          const fullCohort = response.data?.cohort || response.cohort;
                          setSelectedCohort(fullCohort);
                          setShowEditModal(true);
                        } catch (err) {
                          console.error('Error loading cohort details:', err);
                          showToast('Failed to load cohort details', 'error');
                        }
                      }}
                      style={{
                        padding: '0.5rem',
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Edit2 size={16} color="#374151" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCohort(cohort);
                        setShowDeleteModal(true);
                      }}
                      style={{
                        padding: '0.5rem',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 size={16} color="#dc2626" />
                    </button>
                  </div>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '1rem',
                  padding: '1rem',
                  background: '#f9fafb',
                  borderRadius: '0.5rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                      Duration
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
                      {cohort.duration_months} months
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                      Start Date
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
                      {new Date(cohort.start_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                      End Date
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
                      {new Date(cohort.end_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                      Participants
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
                      {cohort.participant_count || 0}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && filteredCohorts.length > 0 && totalPages > 1 && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            total={filteredCohorts.length}
            limit={ITEMS_PER_PAGE}
            hasNext={currentPage < totalPages}
            hasPrev={currentPage > 1}
            onPageChange={(newPage) => {
              setCurrentPage(newPage);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            itemType="cohorts"
          />
        )}

        {/* Modals */}
        <CohortFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateCohort}
          isLoading={saving}
        />

        <CohortFormModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCohort(null);
          }}
          onSave={handleUpdateCohort}
          cohort={selectedCohort}
          isLoading={saving}
        />

        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedCohort(null);
          }}
          onConfirm={handleDeleteCohort}
          title="Delete Cohort"
          message="Are you sure you want to delete this cohort? This will also remove all associated participants and survey data."
          itemName={selectedCohort?.name}
          isLoading={saving}
        />

        {/* Toast Notifications */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default CohortManager;
